import { $, fill, formatElapsed, formatTime, h, newIdempotencyKey } from '../shared/dom.ts';
import { applyLang, initialLang, pick, t, type Bilingual, type Lang, type StringKey } from '../shared/i18n.ts';
import { historyRows, type HistoryEvent, type HistoryJob, type HistoryReceipt } from './history.ts';
import { normalizeOrderNumber } from '../../shared/order-number.ts';
import { passwordInput } from '../shared/password.ts';
import { themeButton } from '../shared/theme.ts';

type Mode = 'order_ready' | 'queue';
type Action = 'call' | 'recall' | 'serve' | 'complete' | 'cancel' | 'no_show' | 'requeue';

interface Service {
  id: string;
  name: Bilingual;
  mode: Mode;
  prefix: string;
  selfService: boolean;
  /** Who last switched the counter code on or off, and when (from the change log). */
  selfServiceChange: { on: boolean; by: string | null; at: string } | null;
}
interface Branch {
  id: string;
  name: Bilingual;
  organization: { id: string; name: Bilingual };
  services: Service[];
  servicePoints: Array<{ id: string; name: Bilingual }>;
}
interface Me {
  /** mustChangePassword: signed in with a password someone gave them; the server waits for their own. */
  user: { displayName: string; isPlatformAdmin: boolean; canManage: boolean; mustChangePassword?: boolean };
  branches: Branch[];
}
interface StaffTicket {
  id: string;
  serviceId: string;
  mode: Mode;
  displayNumber: string;
  externalRef: string | null;
  /** Who made it on the staff screen (null: from the counter sign), and in which shift. */
  createdByName: string | null;
  shift: { key: 'morning' | 'evening' | 'night'; n: number | null } | null;
  status: 'waiting' | 'called' | 'serving' | 'completed' | 'cancelled' | 'no_show' | 'expired';
  servicePoint: Bilingual | null;
  callCount: number;
  createdAt: string;
  lastCalledAt: string | null;
  closedAt: string | null;
  customerOpenedAt: string | null;
  origin: 'counter' | 'staff';
  devices: { webpush: number; liveActivity: number };
  livePages: number;
  /** Audio streams open from the customer's phone (lock-screen mode): the phone is still listening. */
  liveStreams: number;
  lastCall: null | { callCount: number; pushAccepted: number; pushFailed: number; pushPending: number; pushReceived: boolean; pageReceived: boolean; acknowledged: boolean };
  actions: Action[];
}

let lang: Lang = initialLang();
let me: Me | null = null;
/** The password typed to sign in a moment ago, kept on this page only to change it without typing it twice. */
let givenPassword = '';
/** "Forgot your password?" by email (owner, 2026-10-27): offered when this server can send email. */
let resetByEmail = false;
/** Asking for a link ('ask'), or told to check the inbox ('sent'). */
let resetStep: 'ask' | 'sent' | null = null;
/** The token of a link from the email (in the address after #reset=, which never reaches the server's logs). */
const resetFromAddress = () => /^#reset=([A-Za-z0-9_-]{16,64})$/.exec(location.hash)?.[1] ?? null;
let resetToken = resetFromAddress();
/** The last start-up found no connection (it is retried every few seconds). */
let offline = false;
/** The screen's remembered choices. A browser that blocks site storage just doesn't remember; it must not stop the screen from opening. */
const remembered = {
  get(key: string): string {
    try {
      return localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* not remembered on this browser */
    }
  },
};
let branchId = remembered.get('dorek.staff.branch');
let serviceId = remembered.get('dorek.staff.service');
let pointId = remembered.get('dorek.staff.point');
let tickets: StaffTicket[] = [];
/** Ticket whose QR dialog is open, to show live whether the customer has scanned it. */
let qrOpenFor: string | null = null;
let qrAutoClose: number | null = null;
/** Last ticket created from this screen: guards against making a second one by mistake. */
let lastCreated: { id: string; at: number } | null = null;
let es: EventSource | null = null;
let refreshTimer: number | null = null;
const pending = new Set<string>();

const tt = (k: StringKey) => t(k, lang);
/** The eye's words in every password box (owner, 2026-10-27). */
const eyeLabels = () => ({ show: tt('showPassword'), hide: tt('hidePassword') });
const branch = () => me?.branches.find((b) => b.id === branchId) ?? null;
const service = () => branch()?.services.find((s) => s.id === serviceId) ?? null;

// ------------------------------------------------------------------ api

async function api<T = any>(method: string, path: string, body?: unknown, idempotent = false): Promise<{ ok: boolean; status: number; data: T }> {
  const headers: Record<string, string> = { 'x-dorek-csrf': '1' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (idempotent) headers['idempotency-key'] = newIdempotencyKey();
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: 'same-origin' });
  } catch {
    // No connection (a Wi-Fi blip): an ordinary failure, so the caller shows its error and its button
    // works again. Thrown, it left "+ new ticket" locked until a reload (review 2026-10-26).
    return { ok: false, status: 0, data: null as T };
  }
  const data = res.headers.get('content-type')?.includes('json') ? await res.json().catch(() => null) : null;
  if (res.status === 401 && path !== '/api/staff/login') {
    me = null;
    render();
  }
  return { ok: res.ok, status: res.status, data };
}

function toast(msg: string, kind: 'info' | 'error' = 'info'): void {
  const el = $('#toast');
  el.textContent = msg;
  el.className = `toast toast-${kind}`;
  el.hidden = false;
  window.setTimeout(() => (el.hidden = true), 3500);
}

/** "Today 15:40" or "24 October 15:40": when something happened, for people at the counter. */
function whenText(iso: string): string {
  const day = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kuwait' });
  const d = new Date(iso);
  const time = formatTime(iso, lang);
  // Month by name: "24/10" reads backwards inside Arabic text.
  return day(d) === day(new Date()) ? `${tt('today')} ${time}` : `${d.toLocaleDateString(lang === 'ar' ? 'ar-KW-u-nu-latn' : 'en-GB', { day: 'numeric', month: 'long', timeZone: 'Asia/Kuwait' })} ${time}`;
}

function switchedText(c: NonNullable<Service['selfServiceChange']>): string {
  return `${c.on ? tt('switchedOnBy') : tt('switchedOffBy')} ${c.by ?? '—'} · ${whenText(c.at)}`;
}

async function setSelfService(svc: Service, on: boolean): Promise<void> {
  const res = await api('PATCH', `/api/staff/services/${svc.id}`, { selfService: on });
  if (!res.ok) return toast(tt('error_generic'), 'error');
  await refreshMe();
}

/** Fresh services and branches without reconnecting the live feed (the counter code may have changed). */
async function refreshMe(): Promise<void> {
  const res = await api<Me>('GET', '/api/staff/me');
  if (!res.ok) return;
  const before = JSON.stringify(me?.branches);
  me = res.data;
  if (JSON.stringify(me.branches) !== before) render();
}

async function loadMe(): Promise<void> {
  const res = await api<Me>('GET', '/api/staff/me');
  if (res.status === 0) {
    // A tablet that woke before the Wi-Fi: its session is fine, the network isn't there yet.
    offline = true;
    render();
    window.setTimeout(() => void loadMe(), 5000);
    return;
  }
  offline = false;
  me = res.ok ? res.data : null;
  if (!me?.user.mustChangePassword) givenPassword = ''; // only kept while it still has to be changed
  if (me && !me.user.mustChangePassword) {
    if (!branch()) branchId = me.branches[0]?.id ?? '';
    if (!service()) serviceId = branch()?.services[0]?.id ?? '';
    if (!branch()?.servicePoints.some((p) => p.id === pointId)) pointId = branch()?.servicePoints[0]?.id ?? '';
    persist();
    await loadBoard();
    connect();
  }
  render();
}

function persist(): void {
  remembered.set('dorek.staff.branch', branchId);
  remembered.set('dorek.staff.service', serviceId);
  remembered.set('dorek.staff.point', pointId);
}

async function loadBoard(): Promise<void> {
  if (!branchId) return;
  const res = await api<{ tickets: StaffTicket[] }>('GET', `/api/staff/branches/${branchId}/board`);
  if (res.ok) {
    tickets = res.data.tickets;
    renderBoard();
    updateQrStatus();
  }
}

function scheduleRefresh(): void {
  if (refreshTimer) return;
  refreshTimer = window.setTimeout(() => {
    refreshTimer = null;
    void loadBoard();
  }, 150);
}

function connect(): void {
  es?.close();
  if (!branchId) return;
  const feed = new EventSource(`/api/staff/branches/${branchId}/events`);
  es = feed;
  feed.addEventListener('changed', scheduleRefresh);
  feed.addEventListener('hello', scheduleRefresh);
  // The browser retries a dropped feed by itself, but a refused retry (the server restarting during a
  // deploy answers 502) closes it for good: open it again; its "hello" catches the board up.
  feed.onerror = () => {
    if (feed.readyState === EventSource.CLOSED && es === feed && me) window.setTimeout(() => es === feed && connect(), 3000);
  };
}

// ------------------------------------------------------------------ actions

async function login(e: Event): Promise<void> {
  e.preventDefault();
  const form = e.target as HTMLFormElement;
  const fd = new FormData(form);
  const res = await api('POST', '/api/staff/login', { email: fd.get('email'), password: fd.get('password'), remember: fd.get('remember') === 'on' });
  if (!res.ok) {
    toast(tt('loginFailed'), 'error');
    return;
  }
  givenPassword = String(fd.get('password'));
  await loadMe();
}

/** Back to the sign-in form, with the link's part of the address gone (it is spent or useless now). */
function leaveReset(): void {
  resetToken = null;
  resetStep = null;
  if (location.hash) history.replaceState(null, '', location.pathname);
}

/** "Forgot your password?": the email to send a link to (the sign-in form's email is carried over). */
function resetAskForm(): HTMLFormElement {
  const typed = (document.querySelector<HTMLInputElement>('form[data-signin] input[name=email]')?.value ?? '').trim();
  const form = h(
    'form',
    { class: 'panel login', 'data-reset-ask': true, lang },
    h('h1', null, tt('resetAskTitle')),
    h('p', { class: 'muted login-sub' }, tt('resetAskLead')),
    h('label', null, tt('email'), h('input', { name: 'email', type: 'email', autocomplete: 'username', required: true, dir: 'ltr', value: typed })),
    h('button', { class: 'btn btn-primary btn-block', type: 'submit' }, tt('resetSend')),
    h('button', { class: 'btn btn-ghost btn-block', type: 'button', onclick: () => ((resetStep = null), render()) }, tt('backToSignIn')),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = String(new FormData(form).get('email')).trim();
    void api('POST', '/api/staff/password-reset', { email }).then((res) => {
      if (res.ok) {
        resetStep = 'sent';
        return render();
      }
      toast(res.status === 0 ? tt('offlineRetrying') : tt('error_generic'), 'error');
    });
  });
  return form;
}

/** The link from the email: a new password, then everyone signs in again. */
function resetNewForm(token: string): HTMLFormElement {
  const form = h(
    'form',
    { class: 'panel login', 'data-reset-new': true, lang },
    h('h1', null, tt('resetNewTitle')),
    h('p', { class: 'muted login-sub' }, tt('resetNewLead')),
    h('label', null, tt('newPassword'), passwordInput({ name: 'next', autocomplete: 'new-password', required: true, minlength: 10 }, eyeLabels())),
    h('label', null, tt('confirmPassword'), passwordInput({ name: 'confirm', autocomplete: 'new-password', required: true, minlength: 10 }, eyeLabels())),
    h('button', { class: 'btn btn-primary btn-block', type: 'submit' }, tt('savePassword')),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const next = String(fd.get('next'));
    if (next.length < 10) return toast(tt('pwRules'), 'error');
    if (next !== String(fd.get('confirm'))) return toast(tt('pwMismatch'), 'error');
    void api('POST', '/api/staff/password-reset/complete', { token, next }).then((res) => {
      const reason = (res.data as { reason?: string } | null)?.reason;
      if (res.ok || reason === 'invalid') {
        // Done, or the link is no good: either way, back to signing in (every session has ended).
        leaveReset();
        me = null;
        es?.close();
        if (reason === 'invalid') {
          resetStep = 'ask';
          toast(tt('resetLinkInvalid'), 'error');
        } else toast(tt('resetDone'));
        return render();
      }
      toast(reason === 'public_password' ? tt('pwPublic') : res.status === 0 ? tt('offlineRetrying') : res.status === 429 ? tt('error_generic') : tt('pwRules'), 'error');
    });
  });
  return form;
}

/**
 * Signed in with a password someone gave them (a new login, or "new password"): they choose their own
 * before the screen opens; the server answers nothing else until then (owner, 2026-10-26).
 */
function ownPasswordForm(): HTMLFormElement {
  const known = givenPassword; // typed a moment ago on this page; after a reload it is asked again
  const form = h(
    'form',
    { class: 'panel login', 'data-own': true, lang },
    h('h1', null, tt('ownPasswordTitle')),
    h('p', { class: 'muted login-sub' }, tt('ownPasswordLead')),
    known ? null : h('label', null, tt('currentPassword'), passwordInput({ name: 'current', autocomplete: 'current-password', required: true }, eyeLabels())),
    h('label', null, tt('newPassword'), passwordInput({ name: 'next', autocomplete: 'new-password', required: true, minlength: 10 }, eyeLabels())),
    h('label', null, tt('confirmPassword'), passwordInput({ name: 'confirm', autocomplete: 'new-password', required: true, minlength: 10 }, eyeLabels())),
    h('button', { class: 'btn btn-primary btn-block', type: 'submit' }, tt('savePassword')),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const next = String(fd.get('next'));
    if (next.length < 10) return toast(tt('pwRules'), 'error');
    if (next !== String(fd.get('confirm'))) return toast(tt('pwMismatch'), 'error');
    void api('POST', '/api/staff/password', { current: known || String(fd.get('current')), next }).then((res) => {
      if (res.ok) {
        givenPassword = '';
        toast(tt('pwChanged'));
        return void loadMe();
      }
      const reason = (res.data as { reason?: string } | null)?.reason;
      toast(reason === 'wrong_password' ? tt('pwWrong') : reason === 'same_password' ? tt('pwSame') : reason === 'public_password' ? tt('pwPublic') : res.status === 429 ? tt('error_generic') : tt('pwRules'), 'error');
    });
  });
  return form;
}

function openPasswordDialog(): void {
  const form = h(
    'form',
    { class: 'pw-form' },
    h('h2', null, tt('changePasswordTitle')),
    h('label', null, tt('currentPassword'), passwordInput({ name: 'current', autocomplete: 'current-password', required: true }, eyeLabels())),
    h('label', null, tt('newPassword'), passwordInput({ name: 'next', autocomplete: 'new-password', required: true, minlength: 10 }, eyeLabels())),
    h('label', null, tt('confirmPassword'), passwordInput({ name: 'confirm', autocomplete: 'new-password', required: true, minlength: 10 }, eyeLabels())),
    h('button', { class: 'btn btn-primary btn-block', type: 'submit' }, tt('savePassword')),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const next = String(fd.get('next'));
    if (next.length < 10) return toast(tt('pwRules'), 'error');
    if (next !== String(fd.get('confirm'))) return toast(tt('pwMismatch'), 'error');
    void api('POST', '/api/staff/password', { current: String(fd.get('current')), next }).then((res) => {
      if (res.ok) {
        closeModal();
        return toast(tt('pwChanged'));
      }
      const reason = (res.data as { reason?: string } | null)?.reason;
      toast(
        reason === 'wrong_password' ? tt('pwWrong') : reason === 'same_password' ? tt('pwSame') : reason === 'public_password' ? tt('pwPublic') : res.status === 429 ? tt('error_generic') : tt('pwRules'),
        'error',
      );
    });
  });
  modal(form);
}

async function logout(): Promise<void> {
  await api('POST', '/api/staff/logout', {});
  es?.close();
  me = null;
  render();
}

async function createTicket(): Promise<void> {
  const svc = service();
  if (!svc || pending.has('create')) return;
  // The previous ticket hasn't been opened by anyone yet: likely the same customer still scanning. Not
  // asked with the customer screen: it shows both codes, and the next customer is served meanwhile.
  const prev = !usesCustomerScreen() && lastCreated && Date.now() - lastCreated.at < 120_000 ? tickets.find((x) => x.id === lastCreated!.id) : undefined;
  if (prev && prev.status === 'waiting' && !prev.customerOpenedAt && !confirm(tt('confirmUnscanned').replaceAll('{n}', prev.displayNumber))) {
    void openQr(prev);
    return;
  }
  const field = document.getElementById('pos-ref') as HTMLInputElement | null;
  const ref = field?.value.trim();
  // Shops: no ticket without the invoice number (the server refuses it too).
  if (svc.mode !== 'queue' && !ref) {
    toast(tt('posRefNeeded'), 'error');
    field?.focus();
    return;
  }
  pending.add('create');
  const res = await api<{ ticket: StaffTicket; link: string; customerScreenUp?: boolean; reason?: string; number?: string; since?: string; replaced?: { createdAt: string } }>(
    'POST',
    '/api/staff/tickets',
    { serviceId: svc.id, externalRef: ref || undefined },
    true,
  );
  pending.delete('create');
  if (!res.ok) {
    if (res.data?.reason === 'number_in_use') {
      return toast(tt('numberInUse').replaceAll('{n}', res.data.number ?? ref ?? '').replaceAll('{t}', formatTime(res.data.since, lang)), 'error');
    }
    return toast(tt(res.data?.reason === 'ref_required' ? 'posRefNeeded' : 'error_generic'), 'error');
  }
  // Receipts restart each shift: a forgotten ticket from an earlier shift with this number was closed.
  if (res.data.replaced) toast(tt('numberReplaced').replaceAll('{n}', res.data.ticket.displayNumber).replaceAll('{t}', formatTime(res.data.replaced.createdAt, lang)));
  const input = document.getElementById('pos-ref') as HTMLInputElement | null;
  if (input) input.value = '';
  lastCreated = { id: res.data.ticket.id, at: Date.now() };
  await loadBoard();
  // The customer screen shows the code: no window here, the field is ready for the next receipt.
  if (usesCustomerScreen()) {
    if (res.data.customerScreenUp) {
      toast(tt('onCustomerScreen').replaceAll('{n}', res.data.ticket.displayNumber));
      input?.focus();
      return;
    }
    // The tablet is off, asleep or offline: the code here instead, never nowhere (review 2026-11-03).
    toast(tt('customerScreenDown'), 'error');
  }
  showQr(res.data.ticket, res.data.link);
}

async function act(tk: StaffTicket, action: Action): Promise<void> {
  if (action === 'cancel' && !confirm(tt('confirmCancel'))) return;
  if (action === 'no_show' && !confirm(tt('confirmNoShow'))) return;
  const key = `${tk.id}:${action}`;
  if (pending.has(key)) return; // double tap guard (the server also de-duplicates)
  pending.add(key);
  renderBoard();
  const body = tk.mode === 'queue' && pointId && (action === 'call' || action === 'serve') ? { servicePointId: pointId } : {};
  const res = await api<{ error?: string; retryAfterSeconds?: number }>('POST', `/api/staff/tickets/${tk.id}/actions/${action}`, body, true);
  pending.delete(key);
  if (!res.ok) {
    if (res.data?.error === 'recall_too_soon') toast(`${tt('waitFor')} ${res.data.retryAfterSeconds} ${tt('seconds')}`);
    else toast(tt('error_generic'), 'error');
  }
  await loadBoard();
}

async function callNext(): Promise<void> {
  const svc = service();
  if (!svc || pending.has('next')) return;
  pending.add('next');
  const res = await api<{ ticket: StaffTicket | null }>('POST', `/api/staff/services/${svc.id}/call-next`, pointId ? { servicePointId: pointId } : {}, true);
  pending.delete('next');
  if (res.ok && !res.data.ticket) toast(tt('noOneWaiting'));
  else if (!res.ok) toast(tt('error_generic'), 'error');
  await loadBoard();
}

async function openQr(tk: StaffTicket): Promise<void> {
  const res = await api<{ link: string }>('GET', `/api/staff/tickets/${tk.id}/link`);
  if (res.ok) showQr(tk, res.data.link);
}

async function openDisplay(): Promise<void> {
  // The tab opens inside the click itself: one opened after waiting for the server can be taken for
  // a pop-up and blocked, with nothing visible happening (owner, 2026-10-25: "ماتشتغل").
  const tab = window.open('about:blank', '_blank');
  const res = await api<{ url: string }>('GET', `/api/staff/branches/${branchId}/display-link`);
  if (!res.ok) {
    tab?.close();
    toast(tt('error_generic'), 'error');
    return;
  }
  if (tab && !tab.closed) {
    tab.opener = null; // the screen can't reach back into this page
    tab.location.href = res.data.url;
    return;
  }
  // The browser still refused a new tab: offer the link itself (a plain link is never blocked).
  modal(
    h('h2', null, tt('displayScreen')),
    h('p', { class: 'hint' }, tt('displayLinkHint')),
    h('p', null, h('a', { href: res.data.url, target: '_blank', rel: 'noopener', class: 'btn btn-primary', dir: 'ltr' }, tt('openLink'))),
    h('button', { class: 'btn btn-ghost', onclick: () => void navigator.clipboard?.writeText(res.data.url).then(() => toast(tt('copied'))) }, tt('copyLink')),
  );
}

/** Pager-style: type the number on the cup/receipt and press Ready (or Call again if already called). */
async function callByNumber(svc: Service, raw: string): Promise<void> {
  // Same reading as the server: "٥٧", "#57" and " 57" all mean 57.
  const wanted = normalizeOrderNumber(raw);
  if (!wanted) return;
  const tk = tickets.find(
    (x) => x.serviceId === svc.id && (x.status === 'waiting' || x.status === 'called') && (x.displayNumber.toUpperCase() === wanted || x.displayNumber.toUpperCase() === `${svc.prefix}${wanted}`.toUpperCase()),
  );
  if (!tk) return toast(tt('pagerNotFound'), 'error');
  const input = document.getElementById('pager-num') as HTMLInputElement | null;
  if (input) input.value = '';
  await act(tk, tk.status === 'called' ? 'recall' : 'call');
}

/** This device shows a new ticket's code on the customer screen instead of its own window (owner, 2026-11-03). */
const SCAN_SCREEN_KEY = 'dorek.staff.customerScreen';
const usesCustomerScreen = () => remembered.get(SCAN_SCREEN_KEY) === '1';

/**
 * «شاشة الزبون»: the tablet facing the customer. Opened on the tablet from this code (its camera) or
 * the link; the choice to send new tickets' codes there instead of a window here is per device.
 */
async function openCustomerScreen(): Promise<void> {
  if (!branchId) return;
  const res = await api<{ url: string }>('GET', `/api/staff/branches/${branchId}/scan-link`);
  if (!res.ok) return toast(tt('error_generic'), 'error');
  const { url } = res.data;
  const use = h('input', { type: 'checkbox', id: 'use-customer-screen', checked: usesCustomerScreen() });
  use.addEventListener('change', () => remembered.set(SCAN_SCREEN_KEY, use.checked ? '1' : ''));
  modal(
    h('h2', null, tt('customerScreen')),
    h('img', { class: 'qr', src: `/api/staff/branches/${branchId}/scan-qr.svg`, alt: 'QR', width: 240, height: 240 }),
    h('p', { class: 'qr-hint' }, tt('customerScreenHint')),
    h(
      'div',
      { class: 'row' },
      h('a', { href: url, target: '_blank', rel: 'noopener', class: 'btn btn-ghost btn-sm', dir: 'ltr' }, tt('openLink')),
      h('button', { class: 'btn btn-ghost btn-sm', onclick: () => void navigator.clipboard?.writeText(url).then(() => toast(tt('copied'))) }, tt('copyLink')),
    ),
    h('label', { class: 'check' }, use, ` ${tt('customerScreenUse')}`),
    h('p', { class: 'muted hint' }, tt('customerScreenNote')),
  );
}

/** The static counter code customers scan to take a number themselves. */
async function openCounterQr(): Promise<void> {
  const svc = service();
  if (!svc) return;
  const res = await api<{ url: string; selfService: boolean }>('GET', `/api/staff/services/${svc.id}/counter-link`);
  if (!res.ok) return toast(tt('error_generic'), 'error');
  const { url, selfService } = res.data;
  const change = service()?.selfServiceChange ?? null;
  modal(
    h('h2', null, `${tt('counterQr')} — ${pick(svc.name, lang)}`),
    h('img', { class: 'qr', src: `/api/staff/services/${svc.id}/counter-qr.svg`, alt: 'QR', width: 280, height: 280 }),
    h('p', { class: 'qr-hint' }, tt('counterQrHint')),
    h(
      'p',
      { class: `qr-status${selfService ? ' qr-status-ok' : ''}`, id: 'counter-status' },
      selfService ? tt('counterOn') : tt('counterOff'),
    ),
    h(
      'button',
      {
        class: `btn btn-sm ${selfService ? 'btn-ghost' : 'btn-primary'}`,
        onclick: () => {
          if (selfService && !confirm(tt('counterOffConfirm'))) return;
          void setSelfService(svc, !selfService).then(() => openCounterQr());
        },
      },
      selfService ? tt('counterTurnOff') : tt('counterTurnOn'),
    ),
    change ? h('p', { class: 'muted hint' }, switchedText(change)) : null,
    h(
      'div',
      { class: 'row' },
      h('button', { class: 'btn btn-ghost', onclick: () => navigator.clipboard?.writeText(url).then(() => toast(tt('copied'))) }, tt('copyLink')),
      h('a', { class: 'btn btn-ghost', href: url, target: '_blank', rel: 'noopener noreferrer' }, tt('openLink')),
      h('a', { class: 'btn btn-primary', href: `/sign?s=${svc.id}`, target: '_blank', rel: 'noopener' }, tt('printSign')),
    ),
  );
}

// ------------------------------------------------------------------ modals

function modal(...children: Array<Node | null>): void {
  const el = $('#modal');
  el.hidden = false;
  el.replaceChildren(
    h('div', { class: 'modal-card', role: 'dialog', 'aria-modal': 'true' }, ...children, h('button', { class: 'btn btn-ghost', onclick: closeModal }, tt('close'))),
  );
}

function closeModal(): void {
  qrOpenFor = null;
  if (qrAutoClose) clearTimeout(qrAutoClose);
  qrAutoClose = null;
  $('#modal').hidden = true;
  $('#modal').replaceChildren();
}

function showQr(tk: StaffTicket, link: string): void {
  modal(
    h('p', { class: 'qr-num', dir: 'ltr' }, tk.displayNumber),
    h('img', { class: 'qr', src: `/api/staff/tickets/${tk.id}/qr.svg`, alt: 'QR', width: 280, height: 280 }),
    h('p', { class: 'qr-hint' }, tt('scanToFollow')),
    h('p', { class: 'qr-status', id: 'qr-status', role: 'status' }, tt('qrWaitingScan')),
    h(
      'div',
      { class: 'row' },
      h('button', { class: 'btn btn-ghost', onclick: () => navigator.clipboard?.writeText(link).then(() => toast(tt('copied'))) }, tt('copyLink')),
      h('a', { class: 'btn btn-ghost', href: link, target: '_blank', rel: 'noopener noreferrer' }, tt('openLink')),
    ),
  );
  qrOpenFor = tk.id;
  updateQrStatus();
}

/** Shows in the QR dialog when the customer opened the ticket, then closes it. */
function updateQrStatus(): void {
  if (!qrOpenFor || qrAutoClose) return;
  const t = tickets.find((x) => x.id === qrOpenFor);
  const el = document.getElementById('qr-status');
  if (!t?.customerOpenedAt || !el) return;
  el.textContent = tt('qrScanned');
  el.classList.add('qr-status-ok');
  qrAutoClose = window.setTimeout(closeModal, 1800);
}

async function showHistory(tk: StaffTicket): Promise<void> {
  const res = await api<{ events: HistoryEvent[]; notifications: HistoryJob[]; receipts: HistoryReceipt[] }>('GET', `/api/staff/tickets/${tk.id}/history`);
  if (!res.ok) return;
  // One timeline, oldest first, in plain words (see history.ts).
  const rows = historyRows(res.data, lang, tk.mode === 'queue' ? 'queue' : 'order_ready');
  modal(
    h('h2', null, `${tt('history')} — `, h('bdi', null, tk.displayNumber)),
    h(
      'table',
      { class: 'history' },
      h(
        'tbody',
        null,
        ...rows.map((r) =>
          h(
            'tr',
            { class: r.tone ? `h-${r.tone}` : '' },
            h('td', { class: 'h-time' }, h('bdi', null, formatTime(r.at, lang, true))),
            h('td', null, r.text, r.detail ? h('div', { class: 'h-detail' }, r.detail) : null),
          ),
        ),
      ),
    ),
  );
}

// ------------------------------------------------------------------ rendering

function actionLabel(tk: StaffTicket, a: Action): string {
  if (a === 'call') return tt(tk.mode === 'queue' ? 'act_call_queue' : 'act_call_order');
  if (a === 'complete') return tt(tk.mode === 'queue' ? 'act_complete_queue' : 'act_complete_order');
  return tt(`act_${a}` as StringKey);
}

function chip(text: string, kind: 'ok' | 'warn' | 'bad' | 'muted'): HTMLElement {
  return h('span', { class: `chip chip-${kind}` }, text);
}

function indicators(tk: StaffTicket): HTMLElement {
  const chips: HTMLElement[] = [];
  if (tk.customerOpenedAt) chips.push(chip(tt('ind_opened'), 'muted'));
  if (tk.livePages > 0) chips.push(chip(tt('ind_live'), 'ok'));
  if (tk.liveStreams > 0) chips.push(chip(tt('ind_stream'), 'ok'));
  if (tk.devices.webpush > 0) chips.push(chip(`${tt('ind_push')} ×${tk.devices.webpush}`, 'ok'));
  const lc = tk.lastCall;
  if (lc && (tk.status === 'called' || tk.status === 'serving')) {
    if (lc.pushPending) chips.push(chip(tt('ind_pending'), 'warn'));
    if (lc.pushAccepted) chips.push(chip(tt('ind_accepted'), 'ok'));
    if (lc.pushFailed) chips.push(chip(tt('ind_failed'), 'bad'));
    if (lc.pushReceived) chips.push(chip(tt('ind_device'), 'ok'));
    if (lc.pageReceived) chips.push(chip(tt('ind_page'), 'ok'));
    if (lc.acknowledged) chips.push(chip(tt('ind_ack'), 'ok'));
  }
  if (tk.devices.liveActivity > 0) chips.push(chip(tt('ind_livecard'), 'ok'));
  // Nothing can reach the customer's phone: tell staff to fall back to calling out.
  const reachable = tk.livePages > 0 || tk.liveStreams > 0 || tk.devices.webpush > 0 || tk.devices.liveActivity > 0;
  if (tk.status === 'called' && !reachable && !lc?.acknowledged) chips.push(chip(tt('ind_nochannel'), 'bad'));
  return h('div', { class: 'chips' }, ...chips);
}

function card(tk: StaffTicket): HTMLElement {
  // Open: how long the customer has been waiting. Closed: when it closed (a growing count means nothing then).
  const open = ['waiting', 'called', 'serving'].includes(tk.status);
  const when = open || !tk.closedAt
    ? formatElapsed((Date.now() - new Date(tk.createdAt).getTime()) / 60000, lang)
    : `${tt('closedAt')} ${formatTime(tk.closedAt, lang)}`;
  return h(
    'article',
    { class: `tcard st-${tk.status}`, 'data-ticket': tk.displayNumber },
    h(
      'header',
      null,
      h('span', { class: 'tnum', dir: 'ltr' }, tk.displayNumber),
      tk.callCount > 1 ? h('span', { class: 'chip chip-warn' }, `×${tk.callCount}`) : null,
      h('span', { class: `torigin torigin-${tk.origin}` }, tk.origin === 'counter' ? tt('originCounter') : tt('originStaff')),
      h('span', { class: 'tage' }, h('bdi', null, when)),
    ),
    tk.externalRef && tk.externalRef !== tk.displayNumber ? h('p', { class: 'tref' }, `${tt(tk.mode === 'queue' ? 'refQueue' : 'posRef')}: `, h('bdi', null, tk.externalRef)) : null,
    tk.servicePoint ? h('p', { class: 'tref' }, pick(tk.servicePoint, lang)) : null,
    // Every card: when it was made, by whom, in which shift (owner, 2026-10-26).
    h(
      'p',
      { class: 'tmeta' },
      h('bdi', null, formatTime(tk.createdAt, lang)),
      tk.createdByName ? ' · ' : null,
      tk.createdByName ? h('bdi', null, tk.createdByName) : null,
      tk.shift ? ` · ${tt(`shift_${tk.shift.key}`)}${tk.shift.n ? ` ${tk.shift.n}` : ''}` : null,
    ),
    indicators(tk),
    tk.actions.length
      ? h(
          'div',
          { class: 'tactions' },
          ...tk.actions.map((a) =>
            h(
              'button',
              {
                class: `btn btn-sm ${a === 'call' ? 'btn-primary' : a === 'recall' ? 'btn-call' : a === 'cancel' || a === 'no_show' ? 'btn-quiet' : 'btn-ghost'}`,
                'data-action': a,
                disabled: pending.has(`${tk.id}:${a}`),
                onclick: () => void act(tk, a),
              },
              actionLabel(tk, a),
            ),
          ),
        )
      : null,
    h(
      'footer',
      null,
      tk.status === 'waiting' || tk.status === 'called' ? h('button', { class: 'link', onclick: () => void openQr(tk) }, tt('showQr')) : null,
      h('button', { class: 'link', onclick: () => void showHistory(tk) }, tt('history')),
    ),
  );
}

/** Pager base station: a number field + Ready, and one-tap tiles for everyone waiting. */
function pagerStrip(svc: Service): HTMLElement {
  const input = h('input', { id: 'pager-num', inputmode: 'numeric', placeholder: tt('pagerNumber'), dir: 'ltr', autocomplete: 'off', maxlength: 8 });
  const go = () => void callByNumber(svc, input.value);
  input.addEventListener('keydown', (e) => e.key === 'Enter' && go());
  return h(
    'section',
    { class: 'pager' },
    h('div', { class: 'pager-row' }, input, h('button', { class: 'btn btn-call btn-lg', id: 'pager-go', onclick: go }, svc.mode === 'queue' ? tt('pagerCall') : tt('pagerReady'))),
    h('p', { class: 'muted tiles-title' }, tt(svc.mode === 'queue' ? 'tilesTitle_queue' : 'tilesTitle_order')),
    h('div', { class: 'tiles', id: 'tiles' }),
  );
}

function renderTiles(svc: Service): void {
  const host = document.getElementById('tiles');
  if (!host) return;
  const waiting = tickets.filter((x) => x.serviceId === svc.id && x.status === 'waiting');
  fill(
    host,
    ...(waiting.length
      ? waiting.map((tk) => h('button', { class: 'tile', 'data-tile': tk.displayNumber, dir: 'ltr', disabled: pending.has(`${tk.id}:call`), onclick: () => void act(tk, 'call') }, tk.displayNumber))
      : [h('span', { class: 'muted' }, tt('tilesEmpty'))]),
  );
}

function renderBoard(): void {
  const host = document.getElementById('board');
  const svc = service();
  if (!host || !svc) return;
  renderTiles(svc);
  const mine = tickets.filter((x) => x.serviceId === svc.id);
  const col = (key: StringKey, list: StaffTicket[], hint?: StringKey, cap?: number) =>
    h(
      'section',
      { class: `col${hint ? ' col-closed' : ''}` },
      h('h3', null, tt(key), h('span', { class: 'count' }, cap && list.length >= cap ? `${cap}+` : String(list.length))),
      hint ? h('p', { class: 'col-hint' }, tt(hint)) : null,
      h('div', { class: 'col-body' }, ...list.map(card)),
    );
  const byCall = (a: StaffTicket, b: StaffTicket) => (b.lastCalledAt ?? '').localeCompare(a.lastCalledAt ?? '');
  const closed = mine.filter((x) => !['waiting', 'called', 'serving'].includes(x.status)).sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? ''));
  const cols = [
    col(svc.mode === 'queue' ? 'col_waiting_queue' : 'col_waiting_order', mine.filter((x) => x.status === 'waiting')),
    col('col_called', mine.filter((x) => x.status === 'called').sort(byCall)),
  ];
  if (svc.mode === 'queue') cols.push(col('col_serving', mine.filter((x) => x.status === 'serving')));
  // Today's closed numbers stay until the day ends (the server sends the newest 100: BOARD_CLOSED_MAX).
  cols.push(col('col_closed', closed, 'closedHint', 100));
  host.replaceChildren(...cols);
  host.className = `board ${svc.mode === 'queue' ? 'board-4' : 'board-3'}`;
}

function select(id: string, options: Array<{ value: string; label: string }>, value: string, onChange: (v: string) => void): HTMLSelectElement {
  const el = h('select', { id }, ...options.map((o) => h('option', { value: o.value, selected: o.value === value }, o.label)));
  el.addEventListener('change', () => onChange(el.value));
  return el;
}

function render(): void {
  applyLang(lang);
  const app = $('#app');
  const bar = $('#bar-actions');
  fill(
    bar,
    h('button', { class: 'btn btn-ghost btn-sm', onclick: () => ((lang = lang === 'ar' ? 'en' : 'ar'), render()) }, tt('switchLang')),
    themeButton((next) => tt(next === 'dark' ? 'themeDark' : 'themeLight')),
    me?.user.isPlatformAdmin && !me.user.mustChangePassword ? h('a', { class: 'btn btn-ghost btn-sm', href: '/admin' }, tt('ownerConsole')) : null,
    me?.user.canManage ? h('a', { class: 'btn btn-ghost btn-sm', href: '/manage' }, tt('manageBusiness')) : null,
    h('a', { class: 'btn btn-ghost btn-sm', href: '/help', target: '_blank', rel: 'noopener' }, tt('help')),
    me && !me.user.mustChangePassword ? h('button', { class: 'btn btn-ghost btn-sm', onclick: () => openPasswordDialog() }, tt('changePassword')) : null,
    me ? h('button', { class: 'btn btn-ghost btn-sm', onclick: () => void logout() }, tt('logout')) : null,
  );
  // A link from a "forgot your password?" email comes first, signed in or not.
  if (resetToken) {
    if (!app.querySelector(`form[data-reset-new][lang="${lang}"]`)) app.replaceChildren(resetNewForm(resetToken));
    return;
  }
  if (!me && offline) {
    app.replaceChildren(h('section', { class: 'panel', role: 'status' }, h('p', null, tt('offlineRetrying'))));
    return;
  }
  if (!me && resetStep === 'ask') {
    if (!app.querySelector(`form[data-reset-ask][lang="${lang}"]`)) app.replaceChildren(resetAskForm());
    return;
  }
  if (!me && resetStep === 'sent') {
    app.replaceChildren(
      h(
        'section',
        { class: 'panel login', role: 'status' },
        h('h1', null, tt('resetSentTitle')),
        h('p', { class: 'muted login-sub' }, tt('resetSentLead')),
        h('button', { class: 'btn btn-ghost btn-block', type: 'button', onclick: () => ((resetStep = null), render()) }, tt('backToSignIn')),
      ),
    );
    return;
  }
  if (!me) {
    // Already showing, in this language: left as it is. The first "who is signed in?" answer can come
    // after the cashier has typed (a slow shop network) or the browser has filled in the saved
    // password, and drawing the form again emptied it (review 2026-10-26).
    if (app.querySelector(`form[data-signin][lang="${lang}"]`)) return;
    app.replaceChildren(
      h(
        'form',
        { class: 'panel login', 'data-signin': true, lang, onsubmit: (e: Event) => void login(e) },
        h('h1', null, tt('staffTitle')),
        h('p', { class: 'muted login-sub' }, tt('staffSubtitle')),
        h('label', null, tt('email'), h('input', { name: 'email', type: 'email', autocomplete: 'username', required: true, dir: 'ltr' })),
        h('label', null, tt('password'), passwordInput({ name: 'password', autocomplete: 'current-password', required: true }, eyeLabels())),
        h('label', { class: 'check' }, h('input', { name: 'remember', type: 'checkbox' }), tt('rememberDevice')),
        h('button', { class: 'btn btn-primary btn-block', type: 'submit' }, tt('login')),
        // Shown once the server says it can send email (the form isn't drawn again for it).
        h('button', { class: 'btn btn-ghost btn-block', type: 'button', id: 'forgot-email', hidden: !resetByEmail, onclick: () => ((resetStep = 'ask'), render()) }, tt('forgotByEmail')),
        h('p', { class: 'muted hint' }, tt('forgotHint')),
        // An owner has nobody above them in the business: DOREK gives them a new password (owner, 2026-10-26).
        h('p', { class: 'muted hint' }, `${tt('forgotOwner')}: `, h('a', { href: 'mailto:support@getdorek.com', dir: 'ltr' }, 'support@getdorek.com')),
      ),
    );
    return;
  }
  if (me.user.mustChangePassword) {
    // Drawn again only for another language: what is being typed stays (like the sign-in form).
    if (!app.querySelector(`form[data-own][lang="${lang}"]`)) app.replaceChildren(ownPasswordForm());
    return;
  }
  const b = branch();
  const svc = service();
  const toolbar = h(
    'div',
    { class: 'toolbar' },
    select('branch', me.branches.map((x) => ({ value: x.id, label: `${pick(x.organization.name, lang)} · ${pick(x.name, lang)}` })), branchId, (v) => {
      branchId = v;
      serviceId = branch()?.services[0]?.id ?? '';
      pointId = branch()?.servicePoints[0]?.id ?? '';
      persist();
      connect();
      void loadBoard();
      render();
    }),
    b && b.services.length > 1
      ? select('service', b.services.map((s) => ({ value: s.id, label: pick(s.name, lang) })), serviceId, (v) => {
          serviceId = v;
          persist();
          render();
        })
      : null,
    h('button', { class: 'btn btn-ghost btn-sm', onclick: () => void openDisplay() }, tt('displayScreen')),
    h('button', { class: 'btn btn-ghost btn-sm', id: 'customer-screen', onclick: () => void openCustomerScreen() }, tt('customerScreen')),
    h('button', { class: 'btn btn-ghost btn-sm', id: 'counter-qr', onclick: () => void openCounterQr() }, tt('counterQr')),
  );
  const create = h(
    'div',
    { class: 'create' },
    h('input', {
      id: 'pos-ref',
      placeholder: svc?.mode === 'queue' ? tt('refQueueOptional') : tt('posRefRequired'),
      'aria-label': svc?.mode === 'queue' ? tt('refQueueOptional') : tt('posRefRequired'),
      ...(svc?.mode === 'queue' ? {} : { required: true }),
      maxlength: 32,
      inputmode: 'text',
      dir: 'ltr',
      // Enter in the field makes the ticket, like the button.
      onkeydown: (e: Event) => {
        if ((e as KeyboardEvent).key === 'Enter') void createTicket();
      },
    }),
    h('button', { class: 'btn btn-primary btn-lg', id: 'new-ticket', onclick: () => void createTicket() }, `+ ${tt('newTicket')}`),
    svc?.selfService ? h('p', { class: 'hint create-hint' }, tt('twoWaysHint')) : null,
    svc?.mode === 'queue' && b
      ? h(
          'div',
          { class: 'next' },
          b.servicePoints.length
            ? select('point', b.servicePoints.map((p) => ({ value: p.id, label: pick(p.name, lang) })), pointId, (v) => {
                pointId = v;
                persist();
              })
            : null,
          h('button', { class: 'btn btn-call btn-lg', id: 'call-next', onclick: () => void callNext() }, tt('callNext')),
        )
      : null,
  );
  const off = svc && !svc.selfService && svc.selfServiceChange && !svc.selfServiceChange.on ? svc.selfServiceChange : null;
  const banner = off
    ? h(
        'div',
        { class: 'banner banner-warn', role: 'status' },
        h('strong', null, tt('counterOffBanner')),
        h('span', null, switchedText(off)),
        h('button', { class: 'btn btn-sm btn-primary', onclick: () => void setSelfService(svc!, true) }, tt('turnOnNow')),
      )
    : null;
  fill(app, toolbar, banner, create, svc ? pagerStrip(svc) : null, h('div', { id: 'board', class: 'board' }));
  renderBoard();
}

// ------------------------------------------------------------------ boot

$('#modal').addEventListener('click', (e) => {
  if (e.target === $('#modal')) closeModal();
});
document.addEventListener('keydown', (e) => e.key === 'Escape' && closeModal());
// Keep waiting-time labels fresh and recover from any missed live update.
window.setInterval(() => me && !me.user.mustChangePassword && void loadBoard().then(refreshMe), 20_000);
render();
void loadMe();
void api<{ passwordReset?: boolean }>('GET', '/api/public/config').then((res) => {
  resetByEmail = res.ok && res.data?.passwordReset === true;
  document.getElementById('forgot-email')?.toggleAttribute('hidden', !resetByEmail);
});
// A link opened while this page is already open (the same tab).
window.addEventListener('hashchange', () => {
  resetToken = resetFromAddress();
  if (resetToken) render();
});
